import { Margin, P, Button } from '@ovotech/element-v5';

import { AnalyticsClick, useAnalytics, Event } from '@ovotech/ui-tools';
import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom-v5';
import { AnytimeNotEligible } from '@/src/pages/AnytimeEligibilityV2/components/AnytimeNotEligible';
import { PageWithNavigation } from '@/src/components';
import {
  BoltonPlan,
  BoltonPlanStatus,
  boltons,
  EV,
  getPlanNameFromBolton,
  PlanDetails,
  useChargeAnytimeCancelMutation,
  useChargeAnytimeContext,
  useChargeAnytimeProducts,
} from '@monovo/ev-anytime';
import { ROUTE_PLAN } from '@/src/constants/routes';
import { useChargeAnytimeBoltonState } from '@/src/pages/AnytimeEligibilityV2/hooks/useChargeAnytimeBoltonState';
import {
  InitializedConfirmation,
  MonthlyActiveConfirmation,
  PaydActiveConfirmation,
  SwitchingConfirmation,
} from '@/src/pages/AnytimeEligibilityV2/pages/ChargeAnytimeDisconnect/Confirmations';
import { ProductId } from '@/src/types/Products';
import {
  InitializedCancelSuccess,
  MonthlyCancelSuccess,
  PaydCancelSuccess,
  SwitchCancelSuccess,
} from '@/src/pages/AnytimeEligibilityV2/pages/ChargeAnytimeDisconnect/CancelSuccess';
import { Media } from '@/src/components/Media';
import { breakpoints } from '@/src/utils/mediaQuery';
import { SkeletonCard } from '@/src/pages/Home/components/SkeletonCard';
import {
  SpacedBtnGroup,
  ButtonWrapper,
  StyledHeading1,
  FullWidthContentGrid,
  StyledCard,
  StyledHeading,
} from './Cancel.styled';

type ConfirmCancellationType = {
  cancelAnytime: () => void;
  isLoading: boolean;
  boltonPlan: BoltonPlan;
  partnership?: string | null;
};

const getFutureAndCurrentPlanDetails = (
  boltonPlan: BoltonPlan,
  allPlans: PlanDetails[],
): {
  currentPlan: PlanDetails | undefined;
  futurePlan: PlanDetails | undefined;
} => {
  const findPlan = (productType: string | undefined) =>
    productType
      ? Object.values(allPlans).find(p => p.boltonProductType === productType)
      : undefined;

  switch (boltonPlan?.status) {
    case 'Initialized':
      return {
        currentPlan: undefined,
        futurePlan: findPlan(boltonPlan.future?.productType),
      };
    case 'Active':
      return {
        currentPlan: findPlan(boltonPlan.current?.productType),
        futurePlan: undefined,
      };
    case 'Switching':
      return {
        currentPlan: findPlan(boltonPlan.current?.productType),
        futurePlan: findPlan(boltonPlan.future?.productType),
      };
    default:
      return { currentPlan: undefined, futurePlan: undefined };
  }
};

const CancelConfirmationActions = ({
  cancelAnytime,
  ctaText,
}: {
  cancelAnytime: () => void;
  ctaText: string;
}) => {
  const { dispatch: analyticsDispatch } = useAnalytics();

  const history = useHistory();
  const goToPlanPage = () => {
    analyticsDispatch({
      type: 'click',
      name: EV.ELIGIBILITY.CHARGE_ANYTIME_DISCONNECT_CONFIRM_BACK_CLICKED,
      properties: {
        pathname: '/anytime/disconnect',
        journeyName: EV.ELIGIBILITY.CHARGE_ANYTIME_JOURNEY_NAME,
        screen: EV.SCREENS.CONFIRM_CANCELLATION,
      },
    });

    history.push(ROUTE_PLAN);
  };

  return (
    <SpacedBtnGroup>
      <Button
        data-event-name={
          'EVS - EVS OVO Drive Anytime disconnect permission page yes button clicked'
        }
        data-testid="confirm-button"
        onClick={cancelAnytime}
      >
        {ctaText}
      </Button>
      <Button
        data-event-name={
          'EVS - EVS OVO Drive Anytime disconnect permission page no button clicked'
        }
        onClick={goToPlanPage}
        data-testid="reject-button"
        variant="outline"
        iconLeft="chevron-left"
      >
        Back
      </Button>
    </SpacedBtnGroup>
  );
};

const ConfirmCancellation = ({
  cancelAnytime,
  isLoading,
  boltonPlan,
  partnership,
}: ConfirmCancellationType) => {
  const { data: allPlans, isLoading: isLoadingCaProducts } =
    useChargeAnytimeProducts('all');

  if (isLoading || isLoadingCaProducts) {
    return <SkeletonCard testId="skeleton-card" />;
  }

  const { currentPlan, futurePlan } = getFutureAndCurrentPlanDetails(
    boltonPlan,
    allPlans!,
  );

  const renderConfirmationMessage = () => {
    if (currentPlan && futurePlan) {
      return (
        <SwitchingConfirmation
          currentPlan={currentPlan}
          futurePlan={futurePlan}
          partnership={partnership}
        />
      );
    }

    if (currentPlan) {
      return boltons.isMonthlyBolton(boltonPlan.current!.productId) ? (
        <MonthlyActiveConfirmation
          currentPlan={currentPlan}
          partnership={partnership}
        />
      ) : (
        <PaydActiveConfirmation partnership={partnership} />
      );
    }

    if (futurePlan) {
      return (
        <InitializedConfirmation
          futurePlan={futurePlan}
          partnership={partnership}
        />
      );
    }

    return null;
  };

  const title =
    boltonPlan.status === BoltonPlanStatus.Switching
      ? 'Cancel switch'
      : 'Confirm cancellation';

  const ctaButtonText =
    boltonPlan.status === BoltonPlanStatus.Switching
      ? 'Cancel switch'
      : 'Confirm cancellation';

  return (
    <FullWidthContentGrid>
      <StyledHeading1>{title}</StyledHeading1>
      <StyledCard>
        <div data-testid="confirm-cancellation-message">
          {renderConfirmationMessage()}
        </div>
        <Media query={breakpoints.smallDown}>
          <CancelConfirmationActions
            ctaText={ctaButtonText}
            cancelAnytime={cancelAnytime}
          />
        </Media>
      </StyledCard>
      <Media query={breakpoints.smallUp}>
        <CancelConfirmationActions
          ctaText={title}
          cancelAnytime={cancelAnytime}
        />
      </Media>
    </FullWidthContentGrid>
  );
};

const CancelSuccess = ({
  boltonPlan,
  partnership,
}: {
  boltonPlan: BoltonPlan;
  partnership?: string | null;
}) => {
  const { dispatch: analyticsDispatch } = useAnalytics();

  const history = useHistory();
  const goToPlanPage = () => {
    analyticsDispatch({
      type: 'click',
      name: EV.ELIGIBILITY.CHARGE_ANYTIME_DISCONNECT_SUCCESS_BACK_CLICKED,
      properties: {
        pathname: '/anytime/disconnect',
        journeyName: EV.ELIGIBILITY.CHARGE_ANYTIME_JOURNEY_NAME,
        screen: EV.SCREENS.SUCCESS_CANCELLATION,
      },
    });

    history.push(ROUTE_PLAN);
  };
  const { data: allPlans, isLoading: isLoadingCaProducts } =
    useChargeAnytimeProducts('all');

  useEffect(() => {
    analyticsDispatch({
      type: 'view',
      name: EV.ELIGIBILITY.CHARGE_ANYTIME_DISCONNECT_SUCCESS_VIEWED,
      properties: {
        pathname: '/anytime/disconnect',
        journeyName: EV.ELIGIBILITY.CHARGE_ANYTIME_JOURNEY_NAME,
        screen: EV.SCREENS.SUCCESS_CANCELLATION,
      },
    });
  }, []);

  if (isLoadingCaProducts) {
    return <SkeletonCard testId="skeleton-card" />;
  }

  const { currentPlan, futurePlan } = getFutureAndCurrentPlanDetails(
    boltonPlan,
    allPlans!,
  );

  const renderCancellationMessage = () => {
    if (currentPlan && futurePlan) {
      return (
        <SwitchCancelSuccess
          currentPlan={currentPlan}
          futurePlan={futurePlan}
          partnership={partnership}
        />
      );
    }

    if (currentPlan) {
      return boltons.isMonthlyBolton(boltonPlan.current!.productId) ? (
        <MonthlyCancelSuccess
          currentPlan={currentPlan}
          partnership={partnership}
        />
      ) : (
        <PaydCancelSuccess />
      );
    }

    if (futurePlan) {
      return (
        <InitializedCancelSuccess
          futurePlan={futurePlan}
          partnership={partnership}
        />
      );
    }

    return null;
  };

  return (
    <FullWidthContentGrid>
      {currentPlan && futurePlan ? (
        <StyledHeading1>Your switch was cancelled</StyledHeading1>
      ) : (
        <StyledHeading1>You're no longer signed up</StyledHeading1>
      )}
      <StyledCard>
        {renderCancellationMessage()}
        <Media query={breakpoints.smallDown}>
          <ButtonWrapper>
            <Button onClick={goToPlanPage} data-testid="close-button">
              Back to my account
            </Button>
          </ButtonWrapper>
        </Media>
      </StyledCard>
      <Media query={breakpoints.smallUp}>
        <ButtonWrapper>
          <Button onClick={goToPlanPage} data-testid="close-button">
            Back to my account
          </Button>
        </ButtonWrapper>
      </Media>
    </FullWidthContentGrid>
  );
};

const CancelError = () => {
  const { dispatch: analyticsDispatch } = useAnalytics();
  const history = useHistory();

  useEffect(() => {
    analyticsDispatch({
      type: 'view',
      name: EV.ELIGIBILITY.CHARGE_ANYTIME_DISCONNECT_FAILED_VIEWED,
      properties: {
        pathname: '/anytime/disconnect',
        journeyName: EV.ELIGIBILITY.CHARGE_ANYTIME_JOURNEY_NAME,
        screen: EV.SCREENS.FAILED_CANCELLATION,
      },
    });
  }, []);

  const handleBack = () => {
    analyticsDispatch({
      type: 'click',
      name: EV.ELIGIBILITY.CHARGE_ANYTIME_DISCONNECT_FAILED_BACK_CLICKED,
      properties: {
        pathname: '/anytime/disconnect',
        journeyName: EV.ELIGIBILITY.CHARGE_ANYTIME_JOURNEY_NAME,
        screen: EV.SCREENS.FAILED_CANCELLATION,
      },
    });

    history.push(ROUTE_PLAN);
  };

  return (
    <FullWidthContentGrid>
      <StyledHeading1>Something went wrong</StyledHeading1>
      <StyledCard>
        <StyledHeading>Cancellation failed</StyledHeading>
        <P>Go back and try again.</P>
      </StyledCard>
      <Margin top={8}>
        <AnalyticsClick
          name={
            EV.DISCONNECT
              .EVS_OVO_CHARGE_ANYTIME_DISCONNECT_ERROR_PAGE_BACK_CLICKED
          }
        >
          <Button
            data-testid="back-button"
            data-event-name={
              'EVS - EVS OVO Drive Anytime tile cancel button clicked'
            }
            onClick={handleBack}
            variant="outline"
            iconLeft="chevron-left"
          >
            Back
          </Button>
        </AnalyticsClick>
      </Margin>
    </FullWidthContentGrid>
  );
};

export const ChargeAnytimeDisconnect = () => {
  const { dispatch: analyticsDispatch } = useAnalytics();

  const { chargeAnytimeBolton, isFetching, boltonPlan, signupIntegrationType } =
    useChargeAnytimeBoltonState();
  const { data: products } = useChargeAnytimeProducts('all');
  const { partnership: contextPartnership } = useChargeAnytimeContext();

  const rawPartnership = contextPartnership || signupIntegrationType;

  const partnership =
    rawPartnership &&
    rawPartnership.charAt(0).toUpperCase() +
      rawPartnership.slice(1).toLowerCase();

  const chargeAnytimeProductId = chargeAnytimeBolton?.productId;

  const [finalBoltonPlan, setFinalBoltonPlan] =
    React.useState<BoltonPlan | null>(null);

  const [productId, setProductId] = React.useState<ProductId | undefined>(
    chargeAnytimeProductId,
  );

  const isSwitching = boltonPlan?.status === BoltonPlanStatus.Switching;

  useEffect(() => {
    if (boltonPlan && products) {
      analyticsDispatch({
        type: 'view',
        name: EV.ELIGIBILITY.CHARGE_ANYTIME_DISCONNECT_CONFIRM_VIEWED,
        properties: {
          pathname: '/anytime/disconnect',
          journeyName: EV.ELIGIBILITY.CHARGE_ANYTIME_JOURNEY_NAME,
          screen: EV.SCREENS.CONFIRM_CANCELLATION,
        },
      });
    }
  }, [boltonPlan]);

  React.useEffect(() => {
    if (chargeAnytimeProductId) {
      setProductId(chargeAnytimeProductId);
    }
  }, [chargeAnytimeProductId]);

  const {
    isLoading,
    mutate: cancelChargeAnytime,
    isSuccess,
    isError,
  } = useChargeAnytimeCancelMutation();

  const handleCancelChargeAnytime = () => {
    if (boltonPlan && products) {
      analyticsDispatch({
        type: 'submitted' as Event['type'],
        name: EV.ELIGIBILITY.CHARGE_ANYTIME_DISCONNECT_CONFIRM_NEXT_CLICKED,
        properties: {
          cancelledAddOnName: getPlanNameFromBolton(
            boltonPlan[isSwitching ? 'current' : 'future'],
            products,
          ),
          pathname: '/anytime/disconnect',
          journeyName: EV.ELIGIBILITY.CHARGE_ANYTIME_JOURNEY_NAME,
          screen: EV.SCREENS.CONFIRM_CANCELLATION,
        },
      });
    }

    cancelChargeAnytime();
  };

  React.useEffect(() => {
    // If the mutation was successful AND we haven't already saved the plan
    if (isSuccess && !finalBoltonPlan && boltonPlan) {
      // Save the current plan details into our local state
      setFinalBoltonPlan(boltonPlan);
    }
  }, [isSuccess, boltonPlan, finalBoltonPlan]);

  if (!productId) {
    return !isFetching || !isLoading ? <AnytimeNotEligible /> : null;
  }

  const renderContent = () => {
    if (!boltonPlan && !finalBoltonPlan) {
      return null;
    }

    if (isSuccess) {
      return finalBoltonPlan ? (
        <CancelSuccess boltonPlan={finalBoltonPlan} partnership={partnership} />
      ) : (
        <SkeletonCard testId="skeleton-card" />
      );
    }

    if (isError) {
      return <CancelError />;
    }

    return (
      <ConfirmCancellation
        cancelAnytime={handleCancelChargeAnytime}
        isLoading={isLoading}
        boltonPlan={boltonPlan!}
        partnership={partnership}
      />
    );
  };

  return (
    <PageWithNavigation title="Charge Anytime">
      <Margin top={10}>{renderContent()}</Margin>
    </PageWithNavigation>
  );
};
